import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IApplicationState } from 'Store/types'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  GetVisitForUpdate,
  getVisitItem,
  getVisitList,
  UpdateVisitWithId,
} from '../../Actions/action'
import { useTranslation } from 'react-i18next'
import VisitsPaths from 'Application/Visits/Constants/VisitsPaths'
import Icon from 'Components/Icon'
import CircularProgressbar from 'Components/CircularProgressbar'
import { useForm } from 'react-hook-form'
import moment from 'jalali-moment'
import CurrencyInput from 'Components/Form/Input/CurrencyInput'
import BottomSheetDatePickerInput from 'Components/Form/Input/BottomSheetDatePickerInput'
import { useGetInsuranceExpertFieldsCurrentUserQuery } from 'Services/api'

const EditVisits = () => {
  const [t] = useTranslation()
  let { visitId } = useParams()
  const { loading, data } = useSelector((store: IApplicationState) => store.visits.visitItem)

  const { queryParams } = useSelector((store: IApplicationState) => store.visits.visitList)

  const { data: expertData, isSuccess: isExpertSuccess } =
    useGetInsuranceExpertFieldsCurrentUserQuery()

  const dispatch = useDispatch<any>()
  const navigate = useNavigate()

  const [visitStatus, setVisitStatus] = useState<number | null>(null)
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false)

  useEffect(() => {
    dispatch(getVisitItem(visitId))

    const fetchStatus = async () => {
      if (visitId) {
        const result = await dispatch(
          GetVisitForUpdate({ params: { visitId: Number(visitId) } }),
        ).unwrap()
        setVisitStatus(result.visitStatus)
      }
    }

    fetchStatus()
  }, [dispatch, visitId])

  const {
    setValue,
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      newBirthDate: '',
      price: '',
    },
  })

  useEffect(() => {
    if (data) {
      reset({
        newBirthDate: data.birthDate || '',
        price: Number(data.price).toLocaleString('en-US') || '',
      })
    }
  }, [data, reset])

  const getCoverageLimit = (): number => {
    if (isExpertSuccess && !!expertData?.length) {
      const insurance = expertData.find(x => x.insuranceTypeCoreId === 44)
      if (insurance) {
        return insurance.coverageLimit == null ? 999999999999 : insurance.coverageLimit
      }
    }
    return 0
  }

  // const newBirthDate = watch('newBirthDate')
  // const price = useMemo(() => watch('price'), [watch('price')])

  const canEditBirthDate = [1, 3, 4, 7].includes(visitStatus ?? 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center grow">
        <CircularProgressbar />
      </div>
    )
  }

  return (
    <>
      <div className="flex justify-between items-center h-16 px-6 shrink-0">
        <div className="flex justify-between items-center gap-2">
          <div onClick={() => navigate(VisitsPaths.VisitsMain)}>
            <Icon name="arrow-right" className="w-6 h-6 text-gray-700" />
          </div>
          <div className="text-lg font-bold pl-2">{t('EDIT_REQUEST')}</div>
        </div>
      </div>

      <span style={{ marginRight: '25px' }} className="text-sm">
        {t('CHANGE_THE_PREVIOUSLY_ENTERED_VALUES_IF_YOU_WISH')}
      </span>

      {data && (
        <>
          <div className="flex flex-col grow overflow-auto">
            <div className="overflow-auto px-6 pb-6">
              <div style={{ marginTop: '25px' }}>
                <div style={{ marginTop: '30px', marginBottom: '12px' }}>
                  <BottomSheetDatePickerInput
                    id="newBirthDate"
                    name="newBirthDate"
                    label={canEditBirthDate ? t('NEW_DATE_OF_BIRTH') : t('DATE_OF_BIRTH')}
                    format="YYYY-MM-DD"
                    dir="ltr"
                    control={control}
                    onChange={(val: string) => {
                      setValue('newBirthDate', val)
                    }}
                    disabled={!canEditBirthDate}
                  />
                </div>

                {canEditBirthDate && data?.birthDate && (
                  <span
                    style={{ margin: '6px', color: 'GrayText' }}
                    className="text-sm text-gray-50"
                  >
                    {t('PREVIOUS_DATE_OF_BIRTH')}:{moment(data?.birthDate).format('jYYYY/jMM/jDD')}
                  </span>
                )}
              </div>

              <div style={{ marginBottom: '12px', marginTop: '30px' }}>
                <CurrencyInput
                  inputMode="numeric"
                  id="price"
                  name="price"
                  label={t('VALUE_OF_NEW_VEHICLE')}
                  unitText={t('RIAL')}
                  dir="ltr"
                  errors={errors.price}
                  control={control}
                  onChange={(val: string) => {
                    setValue('price', val)
                  }}
                  rules={{
                    max: getCoverageLimit(),
                  }}
                />
              </div>

              <span style={{ margin: '6px', color: 'GrayText' }} className="text-sm text-gray-50">
                {t('VALUE_OF_PREVIOUS_VEHICLE')}:{Number(data?.price).toLocaleString('en-US')}{' '}
                {t('RIAL')}
              </span>
            </div>
          </div>

          <button
            disabled={isUpdateLoading}
            style={{ margin: '10px', marginBottom: '30px', padding: '20px' }}
            className={`btn btn-primary btn-lg`}
            onClick={async () => {
              try {
                setIsUpdateLoading(true)

                await dispatch(
                  UpdateVisitWithId({
                    data: {
                      visitId: Number(visitId),
                      birthDate: getValues('newBirthDate'),
                      price: Number(getValues('price').split(',').join('')),
                    },
                    navigate,
                    t: t('EDIT_REQUEST'),
                  }),
                ).unwrap()

                dispatch({ type: 'Visits/clearVisitList' })
                dispatch(getVisitList({ ...queryParams, pageNumber: 1 }))
              } catch (error) {
                console.log(error)
              } finally {
                setIsUpdateLoading(false)
              }
            }}
          >
            {t('CONFRIM_AND_CONTINUE')}
          </button>
        </>
      )}
    </>
  )
}

export default EditVisits
